import { env } from '@cc-shopping/platform-env';

import { parseImageURL } from './parseImageUrl';
import type { CustomImageWidthProps, ImageCardProps, OptimizedImageProps } from './types';

export default function customImageWidthURLBuilder(
  resizableCardImage: ImageCardProps,
  { customImageWidth, customImageQuality, imageParentWidth }: CustomImageWidthProps,
  alternateImageURL: string,
): OptimizedImageProps {
  const customImageHeight = Number(
    ((resizableCardImage.height * customImageWidth) / resizableCardImage.width).toFixed(2),
  );
  const imageParentHeight = Number(
    ((customImageHeight * imageParentWidth) / customImageWidth).toFixed(2),
  );

  const url = !resizableCardImage?.imagePath
    ? alternateImageURL
    : parseImageURL({
        customImageWidth: customImageWidth,
        customImageQuality: customImageQuality,
        imagePath: resizableCardImage.imagePath,
        alternateImageURL: alternateImageURL,
      });

  return {
    optimized: {
      image: {
        url,
        width: customImageWidth,
        height: isNaN(customImageHeight) ? 120 : customImageHeight,
      },
      parentElement: {
        width: imageParentWidth,
        height: isNaN(imageParentHeight) ? 120 : imageParentHeight,
      },
    },
  };
}

const CDN_IMAGE_PREFIX = '/cdn-cgi/image/';

function getOptionsFragmentIndexesFromURLString(url: string) {
  const startIndex = url.indexOf(CDN_IMAGE_PREFIX) + CDN_IMAGE_PREFIX.length;
  const endIndex = startIndex + url.substring(startIndex).indexOf('/') + 1;
  return { startIndex, endIndex };
}

export function transformCdnImage({
  dpr,
  quality = 80,
  originalHeight,
  originalWidth,
  url,
  sharpen = 1,
  width,
}: {
  dpr?: number;
  quality?: number;
  originalHeight: number;
  originalWidth: number;
  sharpen?: number;
  url: string;
  width: number;
}) {
  const isCdnImage = url.includes(CDN_IMAGE_PREFIX);
  if (!isCdnImage) {
    if (env.NEXT_PUBLIC_DEPLOYABLE_ENV !== 'prod') {
      console.error('Cannot optimize image, it is not a CDN image', url);
    }
    return url;
  }
  if (!dpr) {
    const correspondingHeight = (width * originalHeight) / originalWidth;
    dpr = Math.max(1.5, Number((width / correspondingHeight).toFixed(2)));
  }
  const { startIndex, endIndex } = getOptionsFragmentIndexesFromURLString(url);
  const options = [`format=auto`, `dpr=${dpr}`, `width=${width}`, `quality=${quality}`];
  if (sharpen) {
    options.push(`sharpen=${sharpen}`);
  }
  return `${url.substring(0, startIndex)}${options.join(',')}/${url.substring(endIndex)}`;
}
